import { FieldSet, FormattedString, InputWizardPanelComponent, IpInput, SelectComponent, TextInput } from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { McvcnCreateSidePanel } from "../../components/McvcnCreateSidePanel/McvcnCreateSidePanel";
import { McvcnSelect } from "../../components/McvcnSelect/McvcnSelect";
import { OciCidr } from "../../components/OciCidr/OciCidr";
import { VNETSelect } from "../../components/VNETSelect/VNETSelect";
import { networkOverviewUrl, vnetTroubleshooting } from "../../constants/docConstants";
import { AzureVirtualNetworkSummary } from "../../gen/clients/mchub-azure-api-client";
import { IdResourceType } from "../../helpers/idHelper";
import { FutureMcvcnExisting, FutureMcvcnNew } from "../../helpers/resourceHelper";
import { validateMdsHostName } from "../../helpers/validationHelper";
import { useFeatures } from "../../hooks/useFeatures";

export const GROUP_NETWORK = "network";

const enum NewSidePanels {
  MCVCN = "MCVCN",
}

export const enum Fields {
  HostName = "mdsDbSystemDeploymentDetails.hostnameLabel",
  IpAddress = "mdsDbSystemDeploymentDetails.ipAddress",
  Mcvcn = "multiCloudVirtualNetworkDetails.id",
  VNet = "networkLinkDetails.azureAttachedNetworkIds",
  Cidrs = "networkLinkDetails.ociNetworkCidrs",
  NetworkVirtualAppliance = "networkLinkDetails.customerNvaIpAddress",
}

export interface NetworkTabComponent {
  closeSidePanels: () => void;
}
export interface NetworkTabContentProps {
  componentRef?: (component: NetworkTabComponent) => void;
  inputWizardPanelRef: InputWizardPanelComponent;
  location: string;
  subscriptionId: string;
}

export const NetworkTabContent = ({
  componentRef,
  inputWizardPanelRef,
  location,
  subscriptionId,
}: NetworkTabContentProps): JSX.Element => {
  const { enableMcvcn, enableMdsMcvcnCreate } = useFeatures();
  const [selectRef, setSelectRef] = React.useState<SelectComponent>();
  const [sidePanelOpen, setSidePanelOpen] = React.useState<NewSidePanels | undefined>(undefined);
  const [selectedVnet, setSelectedVnet] = React.useState<AzureVirtualNetworkSummary | undefined>(undefined);

  const closeSidePanels = (): void => setSidePanelOpen(undefined);

  React.useEffect(() => {
    if (componentRef) {
      componentRef({ closeSidePanels } as NetworkTabComponent);
    }
    // Only at mount time. Others props should not have side effect beyond their initial values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onError = (): void => inputWizardPanelRef.showError(Messages.createCommon.loadingErrors.general());

  const onMissingDependencies = (): void => {
    inputWizardPanelRef.showErrorDialog(
      Messages.createCommon.dependencyWarnings.basicsIncomplete.message(),
      Messages.createCommon.dependencyWarnings.basicsIncomplete.title(),
    );
  };

  const onChangeVnet = (_: string, vnet: AzureVirtualNetworkSummary | undefined): void => {
    setSelectedVnet(vnet);
  };

  const onLinkClick = (): void => {
    const missingDependencies: IdResourceType[] = [];
    if (!subscriptionId) missingDependencies.push(IdResourceType.SUBSCRIPTIONS);
    else if (!location) missingDependencies.push(IdResourceType.LOCATION);

    if (missingDependencies.length > 0) {
      onMissingDependencies?.();
    } else {
      setSidePanelOpen(NewSidePanels.MCVCN);
    }
  };

  const createVcnLink = {
    id: "create_new_vcn",
    text: Messages.actions.createNew(),
    onLinkClick,
  };

  const onSubmit = (futureMcvcn: FutureMcvcnExisting | FutureMcvcnNew): void => {
    if (selectRef) {
      // Here is where the consuming form can format the values returned by the panel
      // Just alter the data passed to the setNewOption
      selectRef.setOptionNew(futureMcvcn.name, futureMcvcn);
    }
    setSidePanelOpen(undefined);
  };

  return (
    <>
      <FieldSet header={Messages.createMysql.networkingTab.sectionTitles.databaseSystemNetworking()}>
        <TextInput
          fieldName={Fields.HostName}
          tooltip={Messages.hints.toolTipMySQLHostName()}
          label={Messages.labels.hostNameOnly()}
          validator={validateMdsHostName}
        />
        <IpInput
          fieldName={Fields.IpAddress}
          tooltip={
            FormattedString(
              { inputText: Messages.hints.toolTipMySQLIpAddress(networkOverviewUrl) },
            ) as unknown as string
          }
          label={Messages.labels.ipAddress()}
          validation={{
            containsIpAddress: true,
            containsCidrBlock: false,
          }}
        />
      </FieldSet>
      <FieldSet header={Messages.createMysql.networkingTab.sectionTitles.networkPeering()}>
        {enableMcvcn && enableMdsMcvcnCreate && (
          <>
            <McvcnSelect
              required
              label={Messages.labels.virtualCloudNetwork()}
              componentRef={setSelectRef}
              location={location}
              fieldName={Fields.Mcvcn}
              groupName={GROUP_NETWORK}
              subscriptionId={subscriptionId}
              inputLink={createVcnLink}
            />
            {sidePanelOpen === NewSidePanels.MCVCN && (
              <McvcnCreateSidePanel
                anchorId="main"
                onClose={() => setSidePanelOpen(undefined)}
                onSubmit={onSubmit}
                location={location}
                subscriptionId={subscriptionId}
              />
            )}
          </>
        )}
        {!(enableMcvcn && enableMdsMcvcnCreate) && (
          <>
            <VNETSelect
              required
              fieldName={Fields.VNet}
              label={Messages.labels.virtualNetwork()}
              tooltip={
                FormattedString(
                  { inputText: Messages.hints.tooltipVNET(vnetTroubleshooting) },
                ) as unknown as string
              }
              subscriptionId={subscriptionId}
              location={location}
              onChange={onChangeVnet}
              onError={onError}
              onMissingDependencies={onMissingDependencies}
            />
            <IpInput
              fieldName={Fields.NetworkVirtualAppliance}
              label={Messages.labels.networkVirtualAppliance()}
              tooltip={Messages.hints.tooltipNetworkVirtualAppliance()}
              validation={{
                containsIpAddress: true,
                containsCidrBlock: false,
              }}
            />
            <OciCidr
              required
              fieldName={Fields.Cidrs}
              groupName={GROUP_NETWORK}
              label={Messages.labels.ociCidr()}
              excludeCidrs={selectedVnet?.addressSpaces}
            />
          </>
        )}
      </FieldSet>
    </>
  );
};
